import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import PersonaNew from "@/pages/PersonaNew";
import Chat from "@/pages/Chat";
import Feedback from "@/pages/Feedback";
import Mood from "@/pages/Mood";
import Profile from "@/pages/Profile";
import Pricing from "@/pages/Pricing";
import Account from "@/pages/Account";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Refunds from "@/pages/Refunds";
import Contact from "@/pages/Contact";
import Disclaimer from "@/pages/Disclaimer";
import About from "@/pages/About";
import Admin from "@/pages/Admin";
import PublicSnippet from "@/pages/PublicSnippet";
import DisclaimerModal from "@/components/DisclaimerModal";
import CookieBanner from "@/components/CookieBanner";
import { PaywallProvider, usePaywall } from "@/contexts/PaywallContext";
import PaywallModal from "@/components/PaywallModal";
import { installPaywallInterceptor } from "@/lib/api";
import PublicCard from "@/pages/PublicCard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { useGAPageViews } from "@/lib/analytics";

function RouterInner() {
  const location = useLocation();
  useGAPageViews();
  // CRITICAL: handle Emergent OAuth hash synchronously so we don't accidentally
  // call /auth/me before the session is exchanged.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/personas/new"
        element={
          <ProtectedRoute>
            <PersonaNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:conversationId"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <Feedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mood"
        element={
          <ProtectedRoute>
            <Mood />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/about" element={<About />} />
      <Route path="/s/:slug" element={<PublicSnippet />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        }
      />
      <Route path="/c/:slug" element={<PublicCard />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PaywallProvider>
            <PaywallBridge />
            <RouterInner />
            <PaywallModal />
            <DisclaimerModal />
            <CookieBanner />
            <Toaster position="top-center" richColors closeButton />
          </PaywallProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Tiny bridge that registers the 402 interceptor once the PaywallProvider mounts.
function PaywallBridge() {
  const { open } = usePaywall();
  React.useEffect(() => {
    installPaywallInterceptor(open);
  }, [open]);
  return null;
}

export default App;
